import { postFormData } from '../camera/stagedUpload'
import { rememberThroughput } from '../camera/mediaQuality'

const POSTER_CAPTURE_TIMEOUT_MS = 5000

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.avif']

export const DEFAULT_LIBRARY_ACCEPT =
  'image/*,video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm'

/**
 * Resolve a guest-selected video to the base content-type + extension the
 * Anear API allowlist expects (mp4 / mov / webm). Returns null for formats
 * the platform does not accept. Extension wins over the browser's mime guess.
 */
export function videoTypeForUpload(file) {
  const name = (file?.name || '').toLowerCase()
  const type = (file?.type || '').toLowerCase()
  if (name.endsWith('.mov') || type === 'video/quicktime') return { type: 'video/quicktime', ext: '.mov' }
  if (name.endsWith('.webm') || type === 'video/webm') return { type: 'video/webm', ext: '.webm' }
  if (name.endsWith('.m4v')) return { type: 'video/mp4', ext: '.m4v' }
  if (name.endsWith('.mp4') || type === 'video/mp4') return { type: 'video/mp4', ext: '.mp4' }
  return null
}

function extensionOf(file) {
  const name = (file?.name || '').toLowerCase()
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot) : ''
}

function acceptTokens(accept) {
  return String(accept || '')
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
}

function isImageFile(file) {
  const type = (file?.type || '').toLowerCase()
  if (type.startsWith('image/')) return true
  return IMAGE_EXTENSIONS.includes(extensionOf(file))
}

function acceptAllowsVideo(accept, resolved) {
  const tokens = acceptTokens(accept)
  if (tokens.length === 0) return true
  return tokens.some((token) => {
    if (token === 'video/*') return true
    if (token === resolved.type) return true
    if (token === resolved.ext) return true
    return false
  })
}

function acceptAllowsImage(accept, file) {
  const tokens = acceptTokens(accept)
  if (tokens.length === 0) return true
  const type = (file?.type || '').toLowerCase()
  const ext = extensionOf(file)
  return tokens.some((token) => {
    if (token === 'image/*') return isImageFile(file)
    if (type && token === type) return true
    if (ext && token === ext) return true
    return false
  })
}

/**
 * @returns {{ kind: 'photo' } | { kind: 'video', type: string, ext: string } | null}
 */
export function classifyLibraryFile(file, accept = DEFAULT_LIBRARY_ACCEPT) {
  if (!file) return null
  const video = videoTypeForUpload(file)
  if (video && acceptAllowsVideo(accept, video)) return { kind: 'video', ...video }
  if (isImageFile(file) && acceptAllowsImage(accept, file)) return { kind: 'photo' }
  return null
}

export function libraryVideoSizeError(file, maxUploadMb) {
  const maxBytes = Math.max(1, maxUploadMb) * 1024 * 1024
  if (!file || file.size <= maxBytes) return null
  const mb = (n) => Math.round(n / (1024 * 1024))
  return `That video is too large (${mb(file.size)}MB; max ${mb(maxBytes)}MB). Choose a smaller clip.`
}

/**
 * Library duration rules use min-seconds, and
 * max-upload-seconds when it is greater than 0. A duration of 0 (probe
 * failed) is not an error. max-seconds is a live-record cap and is not applied.
 */
export function libraryDurationError(durationSecs, { minSeconds = 0, maxUploadSeconds = 0 } = {}) {
  const dur = Number(durationSecs) || 0
  if (minSeconds > 0 && dur > 0 && dur < minSeconds) {
    return `That clip is only ${dur}s. Please choose a video at least ${minSeconds}s long.`
  }
  if (maxUploadSeconds > 0 && dur > maxUploadSeconds) {
    return `That video is ${dur}s. Please choose one no longer than ${maxUploadSeconds}s.`
  }
  return null
}

export function probeVideoDuration(file, { timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    let url = null
    let settled = false
    let timeoutId = null

    const finish = (secs) => {
      if (settled) return
      settled = true
      if (timeoutId) window.clearTimeout(timeoutId)
      if (url && typeof URL?.revokeObjectURL === 'function') URL.revokeObjectURL(url)
      resolve(secs)
    }

    try {
      url = URL.createObjectURL(file)
      const video = document.createElement('video')
      video.preload = 'metadata'
      timeoutId = window.setTimeout(() => finish(0), timeoutMs)
      video.addEventListener('error', () => finish(0), { once: true })
      video.addEventListener('loadedmetadata', () => {
        const duration = video.duration
        const secs = Number.isFinite(duration) && duration > 0 ? Math.round(duration) : 0
        finish(secs)
      }, { once: true })
      video.src = url
    } catch (_e) {
      finish(0)
    }
  })
}

export function capturePoster(blob, { timeoutMs = POSTER_CAPTURE_TIMEOUT_MS } = {}) {
  return new Promise((resolve) => {
    let url = null
    let settled = false
    let timeoutId = null

    const finish = (result) => {
      if (settled) return
      settled = true
      if (timeoutId) {
        try {
          window.clearTimeout(timeoutId)
        } catch (_e) {
          // ignore
        }
      }
      if (url && typeof URL?.revokeObjectURL === 'function') URL.revokeObjectURL(url)
      resolve(result)
    }

    try {
      url = URL.createObjectURL(blob)
      timeoutId = window.setTimeout(() => finish(null), timeoutMs)

      const video = document.createElement('video')
      video.muted = true
      video.playsInline = true
      video.preload = 'auto'

      const drawFrame = () => {
        try {
          const w = video.videoWidth || 640
          const h = video.videoHeight || 360
          const canvas = document.createElement('canvas')
          canvas.width = w
          canvas.height = h
          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0, w, h)
          canvas.toBlob((posterBlob) => finish(posterBlob || null), 'image/jpeg', 0.8)
        } catch (_e) {
          finish(null)
        }
      }

      video.addEventListener('error', () => finish(null), { once: true })
      video.addEventListener(
        'loadeddata',
        () => {
          const duration = video.duration
          const target = Number.isFinite(duration) && duration > 0 ? Math.min(1, duration / 2) : 0.1
          video.addEventListener('seeked', drawFrame, { once: true })
          try {
            video.currentTime = target
          } catch (_e) {
            drawFrame()
          }
        },
        { once: true }
      )

      video.src = url
    } catch (_e) {
      finish(null)
    }
  })
}

export async function uploadPresignedAsset({
  onUploadRequest,
  eventId,
  file,
  purpose,
  signal,
  onProgress
}) {
  const upload = await onUploadRequest({ eventId, file, purpose })
  if (!upload?.uploadUrl || !upload?.finalUrl) {
    throw new Error('Invalid upload response')
  }
  if (upload.maxBytes && file.size > upload.maxBytes) {
    const mb = (n) => Math.round(n / (1024 * 1024))
    throw new Error(`Video is too large (${mb(file.size)}MB; max ${mb(upload.maxBytes)}MB). Use a shorter clip.`)
  }

  const formData = new FormData()
  const fields = upload.uploadFields || {}
  Object.keys(fields).forEach((key) => formData.append(key, fields[key]))
  formData.append('file', file)

  await postFormData({ url: upload.uploadUrl, formData, signal, onProgress })
  return { url: upload.finalUrl, key: upload.key || '' }
}

/**
 * Upload a library or recorded clip, then a best-effort poster.
 * Transcode callback runs after the clip is in S3 and before the poster.
 * A missing poster still returns the clip URL.
 */
export async function uploadLibraryClip({
  onUploadRequest,
  eventId,
  blob,
  type,
  ext,
  durationSecs = 0,
  purpose,
  posterPurpose,
  signal,
  onProgress,
  onClipUploaded,
  capturePoster: capturePosterFn = capturePoster,
  maxUploadMb = 250,
  filenameBase
} = {}) {
  if (!onUploadRequest || !eventId) {
    throw new Error('Uploader not ready. Try again in a moment.')
  }

  const sizeError = libraryVideoSizeError(blob, maxUploadMb)
  if (sizeError) throw new Error(sizeError)

  const base = filenameBase || `tribute-${Date.now()}`
  const videoFile = new File([blob], `${base}${ext}`, { type })
  const startedAt = Date.now()
  const { url, key } = await uploadPresignedAsset({
    onUploadRequest,
    eventId,
    file: videoFile,
    purpose,
    signal,
    onProgress
  })
  rememberThroughput({ bytes: videoFile.size, elapsedMs: Date.now() - startedAt })

  if (typeof onClipUploaded === 'function') onClipUploaded(key, url)

  let posterUrl = ''
  let posterKey = ''
  try {
    const posterBlob = await capturePosterFn(blob)
    if (posterBlob) {
      const posterFile = new File([posterBlob], `${base}-poster.jpg`, { type: 'image/jpeg' })
      const posterUpload = await uploadPresignedAsset({
        onUploadRequest,
        eventId,
        file: posterFile,
        purpose: posterPurpose,
        signal
      })
      posterUrl = posterUpload.url
      posterKey = posterUpload.key
    }
  } catch (_e) {
    // Poster is best-effort; never block the clip on it.
  }

  return {
    url,
    key,
    posterUrl,
    posterKey,
    filename: videoFile.name,
    durationSecs
  }
}
